import React, { useContext, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext, AuthProvider } from './contexts/AuthContext';
import { PriceProvider } from './contexts/PriceContext';
import { TransactionProvider } from './contexts/TransactionContext';
import TransactionModal from './components/TransactionModal';
import LandingPage from './pages/LandingPage';
import PrivateApp from './components/PrivateApp';
import './App.css';
import PublicMintPage from './pages/PublicMintPage';
import WalletPage from './pages/WalletPage';

// Component to check authentication for private routes
function PrivateRoutesWrapper() {
  const { isAuthenticated, authStatus, ensName } = useContext(AuthContext);
  const [isChecking, setIsChecking] = useState(true);
  const location = useLocation();

  useEffect(() => {
    console.log('PrivateRoutesWrapper - Auth Status:', {
      isAuthenticated,
      authStatus,
      ensName,
      hasLocalStorageEns: localStorage.getItem('zpost_ensName'),
      currentPath: location.pathname,
      searchParams: location.search
    });

    // Check for stored session on mount
    const checkStoredSession = () => {
      const storedEns = localStorage.getItem('zpost_ensName');
      const storedAddress = localStorage.getItem('zpost_address');
      const storedTimestamp = localStorage.getItem('zpost_authTimestamp');

      if (storedEns && storedAddress && storedTimestamp) {
        const sessionAge = Date.now() - parseInt(storedTimestamp);
        const maxSessionAge = 24 * 60 * 60 * 1000;

        if (sessionAge < maxSessionAge) {
          console.log('Valid stored session found:', storedEns);
        } else {
          console.log('Stored session expired');
          localStorage.removeItem('zpost_ensName');
          localStorage.removeItem('zpost_address');
          localStorage.removeItem('zpost_authTimestamp');
        }
      }

      setIsChecking(false);
    };

    checkStoredSession();
  }, [location, isAuthenticated, authStatus, ensName]);

  // Show loading while checking authentication
  if (isChecking) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 100%)'
      }}>
        <div style={{
          textAlign: 'center',
          padding: '40px',
          background: 'rgba(0, 0, 0, 0.9)',
          borderRadius: '20px',
          border: '2px solid var(--gold)',
          boxShadow: '0 0 40px rgba(255, 215, 0, 0.2)'
        }}>
          <h1 style={{ color: 'var(--gold)', marginBottom: '20px' }}>Zwallet</h1>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '20px' }}>Checking authentication...</p>
          <div style={{
            width: '40px',
            height: '40px',
            margin: '0 auto',
            border: '3px solid var(--gold)',
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  // SPECIAL CASE: Allow access to /wallet when coming from zhat-app
  // Check if this is a wallet route with for=zhat parameter
  const searchParams = new URLSearchParams(location.search);
  const isForZhat = searchParams.get('for') === 'zhat';
  const isWalletRoute = location.pathname.startsWith('/wallet');
  const mode = searchParams.get('mode');

  if (isWalletRoute && isForZhat) {
    console.log('Zhat wallet access detected, allowing bypass for wallet operations');

    // For import/create modes, always allow access (even without existing wallet)
    if (mode === 'import' || mode === 'create') {
      console.log(`Allowing ${mode} mode for zhat, no wallet required`);
      return <PrivateApp />;
    }

    // For unlock/connect modes, check if wallet exists
    const hasWallet = localStorage.getItem('zwallet_created') === 'true';
    const hasAddress = localStorage.getItem('zpost_address');

    if (hasWallet || hasAddress) {
      console.log('Wallet exists, allowing access to wallet page for zhat');
      return <PrivateApp />;
    } else {
      console.log('No wallet found, redirecting to landing for wallet creation');
      return <Navigate to="/" replace />;
    }
  }

  // Standard authentication check for all other routes
  if (!isAuthenticated) {
    console.log('Not authenticated, redirecting to landing page');
    return <Navigate to="/" replace />;
  }

  // Check if user has .zpost.eth domain
  if (isAuthenticated && ensName && !ensName.toLowerCase().endsWith('.zpost.eth')) {
    console.log('User has ENS but not .zpost.eth:', ensName);
  }

  console.log('User authenticated, rendering PrivateApp');
  return <PrivateApp />;
}

// Wrapper for LandingPage to handle redirect when already authenticated
function LandingPageWrapper() {
  const { isAuthenticated, authStatus } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      console.log('User already authenticated, redirecting to home');
      navigate('/home');
    }
  }, [isAuthenticated, navigate]);

  if (authStatus === 'checking-ens' || authStatus === 'connecting') {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 100%)'
      }}>
        <div style={{
          textAlign: 'center',
          padding: '40px',
          background: 'rgba(0, 0, 0, 0.9)',
          borderRadius: '20px',
          border: '2px solid var(--gold)',
          boxShadow: '0 0 40px rgba(255, 215, 0, 0.2)'
        }}>
          <h1 style={{ color: 'var(--gold)', marginBottom: '20px' }}>Zpost</h1>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '20px' }}>
            {authStatus === 'checking-ens' ? 'Verifying .zpost.eth identity...' : 'Connecting...'}
          </p>
          <div style={{
            width: '40px',
            height: '40px',
            margin: '0 auto',
            border: '3px solid var(--gold)',
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
        </div>
      </div>
    );
  }

  return <LandingPage />;
}

// ==================== BACKGROUND WALLET COMPONENT ====================
const BackgroundWallet = () => {
  const { isAuthenticated } = useContext(AuthContext);
  const location = useLocation();

  // Don't show on standalone wallet page (full UI)
  if (location.pathname === '/wallet') {
    return null;
  }

  // Only show when authenticated
  if (!isAuthenticated) {
    return null;
  }

  // Render hidden wallet (modals only)
  return <WalletPage backgroundMode={true} />;
};
// ==================== END BACKGROUND WALLET ====================

function App() {
  return (
    <Router>
      <AuthProvider>
        <PriceProvider>
          <TransactionProvider>
            <Routes>
              {/* Public marketing + onboarding */}
              <Route path="/" element={<LandingPageWrapper />} />
              <Route path="/about" element={<LandingPageWrapper />} />
              <Route path="/features" element={<LandingPageWrapper />} />
              <Route path="/how-it-works" element={<LandingPageWrapper />} />

              {/* Public mint */}
              <Route path="/mint" element={<PublicMintPage />} />

              {/* Public wallet route (for minting popup) */}
              <Route path="/wallet" element={<WalletPage />} />

              {/* Redirect old /ens to authenticated identity page */}
              <Route path="/ens" element={<Navigate to="/home/identity" replace />} />
              <Route path="/ens/*" element={<Navigate to="/home/identity" replace />} />

              {/* ✅ Single private entry point (everything else) */}
              <Route path="/*" element={<PrivateRoutesWrapper />} />
            </Routes>

            {/* Background wallet – persistent on all authenticated pages except standalone /wallet */}
            <BackgroundWallet />

            {/* Global Transaction Modal – appears when a transaction needs confirmation */}
            <TransactionModal />
          </TransactionProvider>
        </PriceProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
